import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Pause, Play, Wand2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Ops Controls — master-admin card grouping two operational levers that
 * are otherwise buried in the API layer:
 *
 *   1. Throttled Domains — view/clear rows in the `throttled_domains`
 *      collection. The 5-min sync loop skips throttled tenants so a
 *      noisy Yabbit account can't starve the event loop.
 *   2. Backfill call_intent — one-click job to populate `call_intent`
 *      on legacy gradings so the Intent Analytics panel + call detail
 *      badge stop showing "unclassified".
 */
const OpsControlsCard = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };

  // ── Throttled domains ─────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(true);

  const loadThrottled = useCallback(async () => {
    setLoadingRows(true);
    try {
      const r = await axios.get(`${API}/admin/throttled-domains`, { headers });
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error('Failed to load throttled domains');
    } finally {
      setLoadingRows(false);
    }
  }, [token]);

  useEffect(() => { loadThrottled(); }, [loadThrottled]);

  const unthrottle = async (domainId) => {
    try {
      await axios.delete(`${API}/admin/throttled-domains/${domainId}`, { headers });
      toast.success('Throttle cleared — domain will resume on next tick');
      loadThrottled();
    } catch (e) {
      toast.error('Failed to clear throttle');
    }
  };

  // ── Backfill call_intent ──────────────────────────────────────────
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillTotalDone, setBackfillTotalDone] = useState(0);
  const [backfillRemaining, setBackfillRemaining] = useState(null);
  const [backfillBreakdown, setBackfillBreakdown] = useState(null);

  const runBackfill = async (loopUntilDone = false) => {
    setBackfillRunning(true);
    setBackfillBreakdown(null);
    let localDone = 0;
    let localCounts = { initial_sales: 0, follow_up: 0, non_sales: 0 };
    try {
      for (let i = 0; i < (loopUntilDone ? 30 : 1); i += 1) {
        // Hard cap the loop at 30 batches (30 × 500 = 15k rows) so a
        // stuck loop never runs forever.
        const r = await axios.post(
          `${API}/admin/backfill/call-intent?limit=500`,
          {},
          { headers, timeout: 60000 },
        );
        const d = r.data || {};
        localDone += d.updated || 0;
        Object.entries(d.intent_counts || {}).forEach(([k, v]) => {
          localCounts[k] = (localCounts[k] || 0) + v;
        });
        setBackfillTotalDone(localDone);
        setBackfillRemaining(d.remaining ?? 0);
        setBackfillBreakdown({ ...localCounts });
        if (!loopUntilDone || (d.remaining ?? 0) === 0 || (d.updated ?? 0) === 0) break;
      }
      toast.success(`Backfilled ${localDone} grading(s)`);
    } catch (e) {
      toast.error('Backfill failed — check backend logs');
    } finally {
      setBackfillRunning(false);
    }
  };

  // ── Transcript extraction backfill (prod one-click) ───────────────
  const [domains, setDomains] = useState([]);
  const [extractDomain, setExtractDomain] = useState('');
  const [extractDays, setExtractDays] = useState(90);
  const [extractRunning, setExtractRunning] = useState(false);
  const [extractResult, setExtractResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/domains`, { headers })
      .then(r => setDomains(r.data?.domains || r.data || []))
      .catch(() => setDomains([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const runExtractionBackfill = async () => {
    if (!extractDomain) { toast.error('Pick a domain first'); return; }
    if (!window.confirm(`Run transcript extraction over the last ${extractDays} days for this domain? Uses LLM tokens; idempotent per call.`)) return;
    setExtractRunning(true);
    setExtractResult(null);
    try {
      const r = await axios.post(
        `${API}/analytics/backfill-call-extraction?domain_id=${extractDomain}&days=${extractDays}`,
        {}, { headers, timeout: 30000 },
      );
      setExtractResult(r.data);
      toast.success('Extraction backfill started in the background');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start extraction backfill');
    } finally {
      setExtractRunning(false);
    }
  };

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="ops-controls-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wand2 className="w-4 h-4 text-teal-500" />
          Ops Controls
          <span className="ml-auto text-[11px] font-medium text-slate-400">
            Master admin only
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Throttled Domains ────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Throttled Domains
              </p>
              <p className="text-[11px] text-slate-500">
                Tenants skipped by the 5-min sync tick. Auto-throttled after 3
                consecutive failures.
              </p>
            </div>
            <button
              onClick={loadThrottled}
              className="text-[11px] text-slate-500 hover:text-slate-800"
              data-testid="throttled-refresh-btn"
            >
              Refresh
            </button>
          </div>
          {loadingRows ? (
            <div className="text-xs text-slate-400 py-3 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div
              className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-center gap-2"
              data-testid="throttled-empty-state"
            >
              <Play className="w-3.5 h-3.5" /> No domains are currently throttled — sync loop is running clean.
            </div>
          ) : (
            <div className="space-y-2" data-testid="throttled-rows">
              {rows.map((r) => (
                <div
                  key={r.domain_id}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-3"
                  data-testid={`throttled-row-${r.domain_id}`}
                >
                  <Pause className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">
                      {r.domain_name || r.domain_id}
                    </p>
                    <p className="text-[11px] text-amber-800 truncate">
                      {r.reason || 'no reason'} · until{' '}
                      {r.throttled_until ? new Date(r.throttled_until).toLocaleTimeString() : '—'}
                      {' · '}
                      by {r.throttled_by || '—'} · {r.error_count} failure{r.error_count === 1 ? '' : 's'}
                    </p>
                    {r.last_error && (
                      <p className="text-[11px] text-amber-700 italic mt-1 line-clamp-2">
                        {r.last_error}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => unthrottle(r.domain_id)}
                    className="text-[11px] font-semibold text-amber-800 hover:text-amber-900 underline underline-offset-2"
                    data-testid={`throttled-clear-${r.domain_id}`}
                  >
                    Clear
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Backfill call_intent ────────────────────────────────── */}
        <div className="border-t border-slate-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Backfill call_intent on legacy gradings
              </p>
              <p className="text-[11px] text-slate-500">
                Populates the intent field on historical rows so the Intent Analytics
                panel stops showing &ldquo;unclassified&rdquo;. Heuristic — no LLM cost.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => runBackfill(false)}
              disabled={backfillRunning}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="backfill-intent-batch-btn"
            >
              {backfillRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Run one batch (500)
            </button>
            <button
              onClick={() => runBackfill(true)}
              disabled={backfillRunning}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="backfill-intent-loop-btn"
              title="Repeat batches until nothing left (max 30 batches / 15k rows per click)"
            >
              {backfillRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Run until done
            </button>
          </div>
          {(backfillTotalDone > 0 || backfillRemaining != null) && (
            <div
              className="mt-3 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2"
              data-testid="backfill-intent-status"
            >
              <p>
                <span className="font-semibold">Updated this session:</span>{' '}
                {backfillTotalDone.toLocaleString()}
                {backfillRemaining != null && (
                  <span className="ml-2 text-slate-500">
                    · {backfillRemaining.toLocaleString()} rows still need backfill
                  </span>
                )}
              </p>
              {backfillBreakdown && (
                <p className="text-slate-500 mt-1">
                  Breakdown → initial_sales: {backfillBreakdown.initial_sales || 0} ·
                  follow_up: {backfillBreakdown.follow_up || 0} ·
                  non_sales: {backfillBreakdown.non_sales || 0}
                </p>
              )}
              {backfillRemaining === 0 && (
                <p className="text-emerald-700 font-medium mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 rotate-180" /> All historical gradings backfilled.
                </p>
              )}
            </div>
          )}
        </div>

        {/* ── Transcript extraction backfill ───────────────────────── */}
        <div className="border-t border-slate-100 pt-4">
          <div className="mb-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-600">
              Transcript extraction backfill
            </p>
            <p className="text-[11px] text-slate-500">
              Runs the appointment / resign / referral extraction over historical transcribed
              calls, then creates follow-up tasks for past wins. Background job — idempotent,
              safe to re-run after deploy.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={extractDomain}
              onChange={(e) => setExtractDomain(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white max-w-[220px]"
              data-testid="extraction-backfill-domain-select"
            >
              <option value="">Select domain…</option>
              {domains.map(d => (
                <option key={d.id} value={d.id}>
                  {d.company?.name || d.yabbit_domain || d.id}
                </option>
              ))}
            </select>
            <input
              type="number" min={1} max={365} value={extractDays}
              onChange={(e) => setExtractDays(Number(e.target.value) || 90)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 w-20"
              title="Days of history to extract"
              data-testid="extraction-backfill-days-input"
            />
            <button
              onClick={runExtractionBackfill}
              disabled={extractRunning || !extractDomain}
              className="text-xs font-semibold px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="extraction-backfill-run-btn"
            >
              {extractRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
              Run extraction backfill
            </button>
          </div>
          {extractResult && (
            <div
              className="mt-3 text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2"
              data-testid="extraction-backfill-status"
            >
              <p>
                <span className="font-semibold">Started.</span>{' '}
                {extractResult.candidates?.toLocaleString?.() ?? extractResult.candidates} transcribed calls in window ·{' '}
                {extractResult.already_extracted?.toLocaleString?.() ?? extractResult.already_extracted} already extracted (skipped).
              </p>
              <p className="text-slate-500 mt-1">{extractResult.note}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OpsControlsCard;
